import { Float32BufferAttribute, type BufferGeometry } from 'three';
import type { Vec3 } from '../../geo/types';

/* Small against every rig piece, far above float rounding: the painter maps are affine, so central
   differences give their Jacobian to rounding. */
const STEP = 1e-3;

function cross(a: Vec3, b: Vec3): Vec3 {
  return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
}

function column(map: (p: Vec3) => Vec3, p: Vec3, axis: number): Vec3 {
  const stepped = (by: number): Vec3 => [
    p[0] + (axis === 0 ? by : 0),
    p[1] + (axis === 1 ? by : 0),
    p[2] + (axis === 2 ? by : 0),
  ];
  const [ahead, behind] = [map(stepped(STEP)), map(stepped(-STEP))];
  return [
    (ahead[0] - behind[0]) / (2 * STEP),
    (ahead[1] - behind[1]) / (2 * STEP),
    (ahead[2] - behind[2]) / (2 * STEP),
  ];
}

/**
 * The unit normal of the surface `map` draws at `p` from the one there before: the map's inverse
 * transpose carries normals, and its cofactors (the cross products of its Jacobian's columns) are
 * that times its determinant, which a swing and a let-down keep positive.
 */
export function drawnNormal(map: (p: Vec3) => Vec3, p: Vec3, normal: Vec3): Vec3 {
  const [a, b, c] = [column(map, p, 0), column(map, p, 1), column(map, p, 2)];
  const [bc, ca, ab] = [cross(b, c), cross(c, a), cross(a, b)];
  const n: Vec3 = [
    normal[0] * bc[0] + normal[1] * ca[0] + normal[2] * ab[0],
    normal[0] * bc[1] + normal[1] * ca[1] + normal[2] * ab[1],
    normal[0] * bc[2] + normal[1] * ca[2] + normal[2] * ab[2],
  ];
  const length = Math.hypot(n[0], n[1], n[2]);
  return [n[0] / length, n[1] / length, n[2] / length];
}

/**
 * What painted.vert adds to each normal of `geometry` at the full painter amount, where the
 * painter view draws vertex i at `map(at(i))`: the drawn normal, turned there by `turn` if given,
 * less the free view's.
 */
export function normalOffsets(
  geometry: BufferGeometry,
  at: (i: number) => Vec3,
  map: (p: Vec3) => Vec3,
  turn?: (drawnAt: Vec3, normal: Vec3) => Vec3
): Float32BufferAttribute {
  const normal = geometry.getAttribute('normal');
  const offsets = new Float32Array(normal.count * 3);
  for (let i = 0; i < normal.count; i++) {
    const own: Vec3 = [normal.getX(i), normal.getY(i), normal.getZ(i)];
    const flat = drawnNormal(map, at(i), own);
    const drawn = turn ? turn(map(at(i)), flat) : flat;
    offsets.set([drawn[0] - own[0], drawn[1] - own[1], drawn[2] - own[2]], i * 3);
  }
  return new Float32BufferAttribute(offsets, 3);
}
